import { User } from '@/types/api/User/User';
import { endpointAuthSystem } from './api';
import { query, queryWithVariables, mutateWithVariables } from './api';
import { Reseller } from '@/types/api/User/Reseller';
import { Supplier } from '@/types/api/User/Supplier';
import { Domain } from '@/types/api/Business/Domain';
import { UserType } from '@/types/api/NewUser/User';
import Cookies from 'js-cookie';
import { Key } from '@/types/constants/Key';
import { UsersWithPaging } from '@/types/api/Raw/user';
import { Role } from '@/types/enum/user';
import { AdminDashboardMetrics } from '@/types/api/User/AdminDashboardMetrics';
import { DomainInputRequestInput } from '@/types/api/User/DomainInputRequest';

export type UserByAuthentikIdType = {
  authentikId: string;
  email: string;
  userId: string;
  username: string;
}

export async function checkUserSession(): Promise<UserType> {
  const api = endpointAuthSystem();
  const formData = new FormData();
  const token = Cookies.get(Key.cookie.access_token_key);
  if (token) formData.append(Key.cookie.access_token_key, token);

  const response = await api.post('api/cekoon/login/authentik/check-user', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data?.UserData?.user
}
// Define a type for the supplier data we're fetching
export interface SupplierForAdmin {
  userId: number;
  username: string;
  firstname: string;
  lastname: string;
  domains: {
    urlSuffix: string;
  }[];
}

export interface UserRoleAndDomain {
  domain: Domain;
  role: Role;
}

export interface Country {
  countryId: number;
  name: string;
  countryCode: string;
}

export const getUserRoleAndDomainByEmail = async (email: string): Promise<UserRoleAndDomain> => {
  const getRoleandDomainResult = await queryWithVariables(
    `
        query ($email: String!) {
          userRoleAndDomainByEmail(email: $email) {
            domain {
              domainId
              logo
              price
              released
              supplierId
              urlSuffix
            },
            role
          }
        }
      `,
    {
      email: email,
    }
  );

  return getRoleandDomainResult.data.userRoleAndDomainByEmail ?? [];
}

export const getUserByEmail = async (email: string): Promise<User> => {
  const getUserResult = await queryWithVariables(
    `
        query ($email: String!) {
          userByEmail (email: $email) {
            userId,
            authentikId,
            username,
            titlePrefix,
            titlePostfix,
            lastname,
            firstname,
            email,
            additionalEmails,
            addresses {
              addressId,
              street,
              houseNr,
              zipCode,
              city
            }
          }
        }
      `,
    {
      email: email,
    }
  );

  return getUserResult.data.userByEmail;
};

export const getUserById = async (userId: number): Promise<User> => {
  const getUserResult = await queryWithVariables(
    `
        query ($userId: Long!) {
          userById (userId: $userId) {
            userId,
            authentikId,
            username,
            titlePrefix,
            titlePostfix,
            lastname,
            firstname,
            email,
            additionalEmails,
          }
        }
      `,
    {
      userId: userId,
    }
  );

  return getUserResult.data.userById;
};

export const getResellerByEmail = async (email: string): Promise<Reseller> => {
  const getUserResult = await queryWithVariables(
    `
        query ($email: String!) {
          resellerByEmail (email: $email) {
            userId,
            authentikId,
            username,
            titlePrefix,
            titlePostfix,
            lastname,
            firstname,
            email,
            supplier {
              userId,
              email,
              domains {
                domainId,
                logo,
                price,
                urlSuffix
              }
            }
          }
        }
      `,
    {
      email: email,
    }
  );

  return getUserResult.data.resellerByEmail;
};

export const getSupplierByEmail = async (email: string): Promise<Supplier> => {
  const getUserResult = await queryWithVariables(
    `
        query ($email: String!) {
          supplierByEmail (email: $email) {
            userId,
            authentikId,
            username,
            titlePrefix,
            titlePostfix,
            lastname,
            firstname,
            email,
            domains {
              domainId,
              logo,
              price,
              urlSuffix
            }
          }
        }
      `,
    {
      email: email,
    }
  );

  return getUserResult.data.supplierByEmail;
};

export const getDomainByUrlSuffix = async (urlSuffix: string): Promise<Domain> => {
  const getDomainByUrlResult = await queryWithVariables(
    `
      query ($urlSuffix: String!) {
        domainByUrlSuffix (urlSuffix: $urlSuffix) {
          domainId,
          price,
          logo,
          urlSuffix,
          companyWebsite,
          colorSchema {
            schemaType,
            hexCode
          },
          packages {
            packageId,
            price,
            description,
            isPublic
          }
        }
      }
    `,
    {
      urlSuffix: urlSuffix,
    }
  );

  return getDomainByUrlResult.data.domainByUrlSuffix;
};

export const getDomainById = async (domainId: number): Promise<Domain> => {
  const getDomainByUrlResult = await queryWithVariables(
    `
      query ($domainId: Long!) {
        domainById(domainId: $domainId) {
          domainId
          price
          logo
          released
          urlSuffix
          companyWebsite
          supplier {
            userId,
            username
          }
          colorSchema {
            colorSchemaId
            schemaType
            hexCode
          }
          packages {
            packageId
            price
            description
          }
        }
      }
    `,
    {
      domainId: domainId,
    }
  );

  return getDomainByUrlResult.data.domainById;
};

export const createDomain = async (domainInput: DomainInputRequestInput): Promise<any> => {
  try {
    const result = await mutateWithVariables(
      `
      mutation ($domainInput: DomainInputRequestInput!) {
        addDomain(domainInput: $domainInput) {
          domainId
          urlSuffix
          price
          released
          supplierId
          colorSchema {
            colorSchemaId
            schemaType
            hexCode
          }
          packages {
            packageId
            description
          }
        }
      }
      `,
      {
        domainInput: domainInput,
      }
    );

    return result.data.addDomain;
  } catch (error) {
    console.error("Error creating domain:", error);
    return null;
  }
};

export const updateDomain = async (domainId: number, domainInput: DomainInputRequestInput): Promise<any> => {
  try {
    const result = await mutateWithVariables(
      `
        mutation UpdateDomain($domainId: Long!, $domainInput: DomainInputRequestInput!) {
          updateDomain(domainId: $domainId, domainInput: $domainInput) {
            domainId
            urlSuffix
            price
            released
            supplierId
            colorSchema {
              colorSchemaId
              schemaType
              hexCode
            }
            packages {
              packageId
              description
            }
          }
        }
      `,
      {
        domainId: domainId,
        domainInput: domainInput,
      }
    );

    return result.data.updateDomain;
  } catch (error) {
    console.error("Error updating domain:", error);
    return null;
  }
};


export const getResellerDomainsByEmail = async (email: string): Promise<Domain[]> => {
  const getUserResult = await queryWithVariables(
    `
        query ($email: String!) {
          resellerByEmail (email: $email) {
            email,
            supplier {
              userId,
              email,
              domains {
                domainId,
                logo,
                price,
                urlSuffix
              }
            }
          }
        }
      `,
    {
      email: email,
    }
  );

  return getUserResult.data?.resellerByEmail?.supplier?.domains ?? [];
};

export async function resolveUserDomains(user: UserType) {
  let domain = (await getUserRoleAndDomainByEmail(user.email)).domain;
  if (!domain) {
    const supplier = await getSupplierByEmail(user.email);
    domain = supplier.domains[0] ?? [];
  }
  return domain;
}

export const getResellerUserIdByEmail = async (email: string): Promise<any> => {
  const getUserResult = await queryWithVariables(
    `
        query ($email: String!) {
          resellerByEmail(email: $email) {
            email
            supplier {
              userId
              email
            }
            __typename
            userId
          }
        }
      `,
    {
      email: email,
    }
  );

  return getUserResult.data?.resellerByEmail ?? null;
};

export const getUserByAuthentikId = async (authentikId: string): Promise<UserByAuthentikIdType> => {
  const getUserResult = await queryWithVariables(
    `
        query UserByAuthentikId($authentikId: String!) {
          userByAuthentikId(authentikId: $authentikId) {
            authentikId
            email
            userId
            username
          }
        }
      `,
    {
      authentikId: authentikId,
    }
  );

  return getUserResult.data?.userByAuthentikId ?? null
}

export const getAllUsers = async (): Promise<User[]> => {
  const result = await queryWithVariables(
    `
      query {
        users {
          userId,
          username,
          email,
          firstname,
          lastname,
          additionalEmails
        }
      }
    `,
    {}
  );
  return result.data.users;
};

export const getAllSuppliers = async (): Promise<SupplierForAdmin[]> => {
  const result = await queryWithVariables(
    `
      query GetAllSuppliers {
        allSuppliers {
          userId
          username
          firstname
          lastname
          domains {
            urlSuffix
          }
        }
      }
    `,
    {}
  );
  return result.data.allSuppliers;
};

export const getAllAvailableCountries = async (): Promise<Country[]> => {
  const result = await queryWithVariables(
    `
      query {
        allCountries {
          countryId,
          name,
          countryCode
        }
      }
    `,
    {}
  );
  return result.data.allCountries;
};

export const getMetricsForAdminDashboard = async (): Promise<AdminDashboardMetrics> => {
  const result = await queryWithVariables(
    `
      query {
        metricsForAdminDashboard {
          domainCount,
          userCount,
          supplierCount
        }
      }
    `,
    {}
  );
  return result.data.metricsForAdminDashboard;
};






const UPDATE_USER_DOMAIN_ID = `
  mutation UpdateUserDomainId($userId: Long!, $domainId: Long) {
    updateUserDomain(userId: $userId, domainId: $domainId) {
      userId
      username
      firstname
      lastname
      domainId
    }
  }
`;

export async function updateUserDomainId({
  userId,
  domainId
}: {
  userId: number;
  domainId: number | null
}): Promise<void> {
  await mutateWithVariables(UPDATE_USER_DOMAIN_ID, { userId, domainId });
}

export const getUsersWithPaging = async ({
  filter,
  search,
  after,
  before,
  first,
  last,
}: {
  filter?: {
    domainId?: number
  }
  search?: string
  after?: string
  before?: string
  first?: number
  last?: number,
}): Promise<UsersWithPaging> => {
  const hasFilter = Object.keys(filter ?? {}).length > 0
  const getUser = await query(
    `
    query GetUsersWithPaging {
      usersWithPaging(
        ${first ? `first: ${first}` : ''}
        ${last ? `last: ${last}` : ''}
        ${after ? `after: "${after}"` : ''}
        ${before ? `before: "${before}"` : ''}
        ${search || hasFilter ? ` where: {
          ${search ? `
          or: [
            { email: { contains: "${search}" } }
            { username: { contains: "${search}" } }
          ],
            `: ''} 
          ${filter?.domainId ? `
          and: [
            { domainId: {eq : ${filter?.domainId}}}
          ]`: ''}
        }`: ''}
    ){
      edges{
        cursor
        node{
          userId
          domainId
          authentikId
          username
          titlePrefix
          titlePostfix
          lastname
          firstname
          email
          additionalEmails
        }
      }
      pageInfo{
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      }
    }
      `,
  );

  return getUser?.data?.usersWithPaging;
};



const SOFT_DELETE_USER = `
  mutation SoftDeleteUser($id: Long!) {
    softDeleteUser(id: $id)
  }
`;

export async function deleteUser({
  userId,
}: {
  userId: number;
}): Promise<void> {
  await mutateWithVariables(SOFT_DELETE_USER, { id: userId });
}